import { useState, useEffect, useCallback, useRef } from "react";
import { useFormikContext } from "formik";
import { connect } from "react-redux";
import { getTopDestinations } from "app/reducers/shopSelector";
import {
	JourneySelections,
	JourneyStepValue,
	useSearchNavigation,
} from "./hooks/useSearchNavigation";
import { SearchDrawer } from "./components/searchDrawer/SearchDrawer";
import { CompactSearch } from "./components/compactSearch/CompactSearch";
import { SearchData, StepId, SearchOption, TravelDatesSelection, TravelMode } from "./types";
import { LocationCombobox } from "./components/locationCombobox/LocationCombobox";
import { DestinationCombobox } from "./components/locationCombobox/DestinationCombobox";
import { differenceInDays } from "date-fns";
import { useTravelDatesAvailability } from "./hooks/useTravelDatesAvailability";
import { bindActionCreators, Dispatch, Action } from "redux";
import {
	fetchSDPAvailableDepartureDate,
	resetSDPAvailableDepartureDate,
} from "app/pages/SmartDP/smartDPActionCreators";
import { TravelDatesCalendar } from "./components/travelDatesCalendar/TravelDatesCalendar";
import { FormattedMessage, useIntl } from "react-intl";
import isEmpty from "lodash/isEmpty";
import { isStepValueEmpty } from "app/features/SearchEngine/searchJourney/utils/isStepValueEmpty";
import { FETCH_AVAILABILITIES_STATUS } from "app/features/SearchEngine/searchJourney/hooks/useSDPTravelDatesAvailability";
import { buildSdpRotationUri } from "app/features/SearchEngine/searchJourney/utils/buildSdpRotationUri";

type DepartureCity = {
	code: string;
	label: string;
	airportIATAs: string[];
	isMain?: boolean;
	mainOrder?: number;
};

type ReduxState = {
	smartDP: {
		destinations: Destination[];
		departureCities: DepartureCity[];
		availabilitiesDate: Record<string, { duration?: number; ed?: string }[]>;
	};
};

export type Destination = {
	code: number;
	label: string;
	labels: string[];
	levels: {
		code: number;
		level: string;
		label: string;
	}[];
	type: string;
	airportIATAs: {
		name: string;
		isMain: boolean;
	}[];
	departureDateStart: number;
	departureDateEnd: number;
	departureCities: {
		code: string;
		uri: string;
	}[];
	requestable: boolean;
	toSuggest: boolean;
	suffix?: string;
};

type Step = {
	id: StepId;
	title: string;
	render?: (
		props: {
			currentValues: SearchData;
			onSelect: (value: JourneyStepValue) => void;
			onConfirm: (value: JourneyStepValue) => void;
		} & Record<string, unknown>
	) => React.ReactNode;
};

type StepName = "departure_city" | "destination" | "travel_dates";

type OwnProps = {
	travelMode: TravelMode;
	steps: Step[];
	onStepConfirm?: (
		stepName: StepName,
		stepValue: JourneyStepValue | null,
		meta?: { showChipMenu: boolean }
	) => void;
};
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type SearchJourneyComponentProps = OwnProps & StateProps & DispatchProps;

const SearchJourneyComponent = ({
	travelMode,
	availabilities,
	steps,
	departureCities,
	topDepartureCities,
	destinations,
	topDestinations,
	fetchAvailability,
	resetAvailability,
	onStepConfirm,
}: SearchJourneyComponentProps) => {
	const { formatMessage } = useIntl();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [transitionDirection, setTransitionDirection] = useState<"forward" | "backward">(
		"forward"
	);
	const { values, setValues, setFieldValue, setErrors } = useFormikContext<SearchData>();

	const justSelectedLocationRef = useRef(false);
	const valuesRef = useRef(values);
	valuesRef.current = values;
	const stepsRef = useRef(steps);
	stepsRef.current = steps;

	const stepsIds = steps.map(step => step.id);

	const initialSelections = steps.reduce<JourneySelections>((acc, step) => {
		acc[step.id] = values[step.id] as JourneyStepValue;
		return acc;
	}, {});

	const {
		currentStepId,
		selections,
		updateSelection,
		updateSelectionForStep,
		next,
		back,
		startFlow,
		cancelFlow,
		canBack,
	} = useSearchNavigation(stepsIds, initialSelections);

	const currentValues = steps.reduce<Partial<Record<StepId, JourneyStepValue>>>((acc, step) => {
		acc[step.id] = (selections[step.id] || values[step.id]) as JourneyStepValue;
		return acc;
	}, {}) as SearchData;

	const departureCityCode =
		typeof currentValues.departureCity === "object" && "code" in currentValues.departureCity
			? currentValues.departureCity.code
			: undefined;

	const destinationResortCode =
		typeof currentValues.destinationResort === "object" &&
		"code" in currentValues.destinationResort
			? currentValues.destinationResort.code
			: undefined;

	const departureDate =
		typeof currentValues.travelDates === "object" &&
		"departureDate" in currentValues.travelDates
			? currentValues.travelDates.departureDate
			: undefined;
	const endDate =
		typeof currentValues.travelDates === "object" && "endDate" in currentValues.travelDates
			? currentValues.travelDates.endDate
			: undefined;

	const uri = buildSdpRotationUri(currentValues.destinationResort, departureCityCode);

	const clearDates = useCallback(() => {
		setValues(currentValues => ({
			...currentValues,
			travelDates: undefined,
			duration: undefined,
		}));

		updateSelectionForStep("travelDates", "");

		// @see note 1 in ./docs/README.md
		if (justSelectedLocationRef.current) {
			justSelectedLocationRef.current = false;

			const formValues = valuesRef.current;
			const depCityCode =
				typeof formValues.departureCity === "object" && "code" in formValues.departureCity
					? formValues.departureCity.code
					: undefined;
			const destCode =
				typeof formValues.destinationResort === "object" &&
				"code" in formValues.destinationResort
					? formValues.destinationResort.code
					: undefined;

			if (depCityCode && destCode) {
				const selectionsForFlow = stepsRef.current.reduce<JourneySelections>(
					(acc, step) => {
						acc[step.id] =
							step.id === "travelDates"
								? ""
								: (formValues[step.id] as JourneyStepValue);
						return acc;
					},
					{}
				);
				setTransitionDirection("forward");
				startFlow("travelDates", selectionsForFlow);
				setIsDrawerOpen(true);
			}
		}
	}, [setValues, updateSelectionForStep, startFlow]);

	const { minDate, maxDate, availabilitiesStatus, isDateDisabled } = useTravelDatesAvailability({
		travelMode,
		uri,
		departureCity: departureCityCode !== undefined ? String(departureCityCode) : undefined,
		destinationResort:
			destinationResortCode !== undefined ? String(destinationResortCode) : undefined,
		departureDate,
		returnDate: endDate,
		availabilities,
		fetchAvailability,
		resetAvailability,
		clearDates,
	});

	const currentStep = steps.find(step => step.id === currentStepId);

	const getInitialSelectionsFromValues = () =>
		steps.reduce<JourneySelections>((acc, step) => {
			const fieldValue = values[step.id];

			acc[step.id] = fieldValue as JourneyStepValue;

			return acc;
		}, {});

	const handleOpenDrawer = (id: StepId) => {
		setTransitionDirection("forward");
		startFlow(id, getInitialSelectionsFromValues());
		setIsDrawerOpen(true);
	};

	const handleSelect = (
		value: JourneyStepValue,
		additionalSelections?: Record<string, unknown>
	) => {
		if (!currentStepId) {
			return;
		}

		const action = next(value);

		if (action === "CONTINUE") {
			setTransitionDirection("forward");
			setFieldValue(currentStepId, value);
		}

		if (action === "CLOSE") {
			// @see note 1 in ./docs/README.md
			if (currentStepId === "departureCity" || currentStepId === "destinationResort") {
				justSelectedLocationRef.current = true;
			}

			const finalSelections = {
				...selections,
				[currentStepId]: value,
			};

			setValues(
				values =>
					({
						...values,
						...finalSelections,
						...additionalSelections,
					} as SearchData)
			);

			setErrors({});
			cancelFlow();
			setIsDrawerOpen(false);
		}
	};

	useEffect(() => {
		const { endDate = undefined, departureDate = undefined } = values.travelDates || {};

		if (!endDate || !departureDate) {
			return;
		}

		setFieldValue("duration", {
			code: differenceInDays(endDate, departureDate),
		});
	}, []);

	const STEP_COMPONENTS = {
		departureCity: (
			<LocationCombobox
				id="departureCity"
				autoFocus
				placeholder={formatMessage({
					id: "sdp.search.departure.city.input.label.mobile",
				})}
				noOptionsMessage={() => {
					return <FormattedMessage id="sdp.search.no.result.label" />;
				}}
				label={formatMessage({
					id: "sdp.search.departure.city.input.label",
				})}
				value={selections.departureCity as SearchOption}
				onChange={(value, { showChipMenu }) => {
					handleSelect(value || "");

					if (onStepConfirm) {
						onStepConfirm("departure_city", value, {
							showChipMenu,
						});
					}
				}}
				options={departureCities}
				optionsWhenEmpty={topDepartureCities}
			/>
		),
		destinationResort: (
			<DestinationCombobox
				id="destinationResort"
				autoFocus
				menuListMaxHeight="72vh"
				placeholder={formatMessage({
					id: "sdp.search.destination.input.label.mobile",
				})}
				noOptionsMessage={() => {
					return <FormattedMessage id="sdp.search.no.result.label" />;
				}}
				label={formatMessage({
					id: "sdp.search.destination.input.label",
				})}
				value={selections.destinationResort as SearchOption}
				onChange={(value, { showChipMenu }) => {
					handleSelect(value || "");

					if (onStepConfirm) {
						onStepConfirm("destination", value, {
							showChipMenu,
						});
					}
				}}
				options={destinations}
				optionsWhenEmpty={topDestinations}
			/>
		),
		travelDates: (
			<TravelDatesCalendar
				value={currentValues.travelDates}
				onChange={(nextValue: TravelDatesSelection) => {
					updateSelection(nextValue);
				}}
				travelMode={travelMode}
				hasRequiredData={
					travelMode === "hotelonly"
						? Boolean(destinationResortCode)
						: Boolean(departureCityCode && destinationResortCode)
				}
				onConfirm={(nextValue: TravelDatesSelection) => {
					if (!nextValue.endDate || !nextValue.departureDate) {
						return;
					}

					handleSelect(nextValue, {
						duration: {
							code: differenceInDays(nextValue.endDate, nextValue.departureDate),
						},
					});

					if (onStepConfirm) {
						onStepConfirm("travel_dates", nextValue);
					}
				}}
				minDate={minDate}
				maxDate={maxDate}
				hasNoDates={
					travelMode === "sdp" &&
					isEmpty(availabilities) &&
					availabilitiesStatus === FETCH_AVAILABILITIES_STATUS.SUCCESS
				}
				isLoading={availabilitiesStatus === FETCH_AVAILABILITIES_STATUS.LOADING}
				isDateDisabled={isDateDisabled}
			/>
		),
	};

	return (
		<>
			<CompactSearch stepIds={stepsIds} onClick={handleOpenDrawer} />

			<SearchDrawer
				isOpen={isDrawerOpen}
				onClose={() => {
					// if a field is empty, we clear the value in the form when the drawer is closed
					if (currentStepId && isStepValueEmpty(selections[currentStepId])) {
						setFieldValue(currentStepId, "");
					}
					cancelFlow();
					setIsDrawerOpen(false);
				}}
				title={currentStep ? currentStep.title : ""}
				hasPreviousStep={canBack}
				onPrevious={() => {
					setTransitionDirection("backward");
					back();
				}}
				transitionDirection={transitionDirection}
				transitionKey={currentStepId || "closed"}
				paddedBody={currentStepId !== "travelDates"}
			>
				{currentStepId ? STEP_COMPONENTS[currentStepId] : null}
			</SearchDrawer>
		</>
	);
};

const mapStateToProps = (state: ReduxState) => {
	const departureCities = state.smartDP.departureCities;
	const topDepartureCities = departureCities
		.filter((option: DepartureCity) => option.isMain === true)
		.slice()
		.sort((a: DepartureCity, b: DepartureCity) => {
			const orderA = a.mainOrder === undefined ? Number.MAX_SAFE_INTEGER : a.mainOrder;
			const orderB = b.mainOrder === undefined ? Number.MAX_SAFE_INTEGER : b.mainOrder;
			return orderA - orderB;
		});

	return {
		availabilities: state.smartDP.availabilitiesDate,
		departureCities,
		topDepartureCities,
		destinations: state.smartDP.destinations,
		topDestinations: getTopDestinations(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) =>
	bindActionCreators(
		{
			fetchAvailability: fetchSDPAvailableDepartureDate,
			resetAvailability: resetSDPAvailableDepartureDate,
		},
		dispatch
	);

export const SearchJourney = connect(
	mapStateToProps,
	mapDispatchToProps
)(SearchJourneyComponent as React.ComponentType<SearchJourneyComponentProps>);
